import {
  dayKey,
  isBedtime,
  isNight,
  levelForXp,
  localMinutes,
  messLevel,
  visibleItems,
  type GameEvent,
  type GameState,
} from "@squire/core";
import type { AwayPlace } from "@squire/sprites";
import { Director, type ConversationMode } from "../avatar/director.js";
import { FrameLoop } from "../system/loop.js";
import { createSceneCanvas } from "./canvas.js";
import { SceneRenderer } from "./scene.js";

export interface SceneViewOptions {
  clock?: () => number;
  fps?: number;
  random?: () => number;
  /** Wall profile: dim at night. */
  nightDim?: boolean;
}

/** The living apartment: canvas + renderer + avatar director + capped loop. */
export class SceneView {
  readonly director: Director;
  readonly loop: FrameLoop;
  readonly canvas: HTMLCanvasElement;
  private readonly renderer: SceneRenderer;
  private readonly disposeCanvas: () => void;
  private state: GameState;
  private away = false;
  private wakeUntil = 0;
  /** Last touch, talk or camera motion (outings only start after a quiet while). */
  private lastInteraction = Date.now();
  private readonly clock: () => number;
  private readonly nightDim: boolean;
  /** Seconds-level timestamp of the last frame, for the watchdog. */
  lastFrameAt = 0;

  constructor(host: HTMLElement, state: GameState, opts: SceneViewOptions = {}) {
    this.state = state;
    this.clock = opts.clock ?? (() => Date.now());
    this.nightDim = opts.nightDim ?? true;
    const { canvas, ctx, dispose } = createSceneCanvas(host);
    this.canvas = canvas;
    this.disposeCanvas = dispose;
    this.renderer = new SceneRenderer(ctx);
    this.director = new Director(opts.random);
    this.loop = new FrameLoop(() => this.frame(), opts.fps ?? 10, this.clock);
  }

  start(): void {
    this.loop.start();
  }

  stop(): void {
    this.loop.stop();
    this.disposeCanvas();
    this.canvas.remove();
  }

  setState(state: GameState): void {
    this.state = state;
  }

  setAway(away: boolean): void {
    this.away = away;
  }

  setConversation(mode: ConversationMode): void {
    this.director.conversation = mode;
  }

  handleEvents(events: readonly GameEvent[]): void {
    this.director.handleEvents(events, this.state, this.clock());
  }

  cacheSize(): number {
    return this.renderer.stats().frames;
  }

  /** Debug mode: drive the avatar directly. */
  debugAnim(anim: string, ms: number): void {
    this.director.debugAnim(anim, this.clock(), ms);
  }

  debugGo(place: AwayPlace | "out" | "home", ms: number, friend: boolean): void {
    this.director.debugGo(place, this.clock(), ms, friend);
  }

  /** Touch or motion at night lifts the dimming for a while. */
  wake(ms = 60_000): void {
    const now = this.clock();
    this.wakeUntil = now + ms;
    this.lastInteraction = now;
    this.director.comeBack(now);
  }

  /** OLED care: shift the whole scene by a few screen pixels every few minutes. */
  applyPixelShift(now: number): void {
    const step = Math.floor(now / (4 * 60_000)) % 4;
    const offsets = [
      [0, 0],
      [3, 0],
      [3, 3],
      [0, 3],
    ] as const;
    const [x, y] = offsets[step]!;
    this.canvas.style.transform = `translate(${x}px, ${y}px)`;
  }

  private frame(): void {
    const now = this.clock();
    const s = this.state;
    const tz = s.settings.timezone;
    const night = isNight(s, now);
    // In bed from bedtime (an hour after active hours) until morning; the screen dims only at night.
    const ctx = {
      state: s,
      now,
      night: isBedtime(s, now) && now > this.wakeUntil,
      away: this.away,
      quietForMs: now - this.lastInteraction,
    };
    this.director.update(ctx);
    const avatar = this.director.frame(ctx);
    const owned = s.inventory.map((i) => i.id).filter((id) => !this.director.pendingItems.has(id));
    const minutes = localMinutes(now, tz);
    const day = dayKey(now, tz);
    const dim = this.nightDim && night && now > this.wakeUntil ? 0.55 : 0;
    this.renderer.draw({
      room: {
        minutes: minutes - (minutes % 5),
        items: visibleItems(owned),
        mess: messLevel(s, now),
        doorOpen: avatar.doorOpen,
        bulbOn: !night && (minutes >= 18 * 60 || minutes < 7 * 60) ? true : night && now <= this.wakeUntil,
        shopping: s.shopping.filter((i) => !i.done).length,
        mindLevel: levelForXp(s.stats.xp.mind),
        seed: Number(day.replace(/-/g, "")) % 97,
        awayNote: this.away,
      },
      look: s.look,
      avatar,
      now,
      dim,
    });
    this.applyPixelShift(now);
    this.lastFrameAt = now;
  }
}
